import { KeyboardEvent, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Box, Button, ButtonGroup, Typography } from '@mui/material';
import {
  Estate,
  EstateImport_GetAllEstateImportsByPortalIdDocument,
  EstateImportStatus,
  useDecisionVault_LinkMatterMutation,
  useDecisionVault_SearchMattersLazyQuery,
  useDecisionVault_SyncMattersMutation,
} from 'queries';

import FormTextField from 'common/form-text-field';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import { useStyles } from './sync-decision-vault-modal-form.styles';
import { SyncDecisionVaultModalFormSearchResultGrid } from './sync-decision-vault-modal-form-search-result-grid';

type Props = {
  onExitClick: () => void;
  selectedEstate?: Estate;
};

type SyncDecisionVaultMattersFormValues = {
  matterName: string;
  selectedMatterIds: string[];
};

export const SyncDecisionVaultModalForm = ({ selectedEstate, onExitClick }: Props) => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();
  const [selectedMatterIds, setSelectedMatterIds] = useState<string[]>([]);
  const [isSyncingSelectMatters, setIsSyncingSelectMatters] = useState(
    selectedEstate ? true : false,
  );

  const [
    searchDecisionVaultMatters,
    { data: mattersData, loading: syncDecisionVaultMattersExecuting },
  ] = useDecisionVault_SearchMattersLazyQuery({
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
  });

  const [syncDecisionVaultMatters, { loading: isLoadingMattersData }] =
    useDecisionVault_SyncMattersMutation({
      refetchQueries: [
        {
          query: EstateImport_GetAllEstateImportsByPortalIdDocument,
          variables: {
            input: {
              portalId: portalId,
              filter: {
                status: EstateImportStatus.Pending,
              },
            },
          },
        },
      ],
      onCompleted: (data) => {
        if (!data.data.ok) {
          showAlert({
            message: `No submitted matters found.`,
            severity: NotificationSeverity.INFO,
          });
          return;
        }
        showAlert({
          message: `Matters have been synced successfully.`,
          severity: NotificationSeverity.SUCCESS,
        });
      },
      onError: (e) => {
        showAlert({
          message: `An issue occurred while syncing your data : ` + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [linkDecisionVaultMatterToExistingMatter, { loading: linkDecisionVaultMatterExecuting }] =
    useDecisionVault_LinkMatterMutation({
      refetchQueries: [
        {
          query: EstateImport_GetAllEstateImportsByPortalIdDocument,
          variables: {
            input: {
              portalId: portalId,
              filter: {
                status: EstateImportStatus.Pending,
              },
            },
          },
        },
      ],
      onCompleted: () => {
        showAlert({
          message: `Matter has been linked.`,
          severity: NotificationSeverity.INFO,
        });
      },
      onError: (e) => {
        showAlert({
          message: `An issue occurred while syncing your data : ` + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const { handleSubmit, control, getValues } = useForm<SyncDecisionVaultMattersFormValues>();

  const onKeyDownHandler = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSubmit();
    }
  };

  const onSubmit = () => {
    searchDecisionVault();
  };

  const searchDecisionVault = () => {
    searchDecisionVaultMatters({
      variables: {
        input: {
          portalId: portalId,
          searchParam: getValues('matterName') || '',
        },
      },
    });
  };

  const onSyncAllClick = async () => {
    await syncDecisionVaultMatters({
      variables: {
        input: {
          portalId,
        },
      },
    });
    // close the modal when syncing all matters
    onExitClick();
  };

  const onSyncSelectedClick = () => {
    if (selectedEstate) {
      linkDecisionVaultMatterToExistingMatter({
        variables: {
          input: {
            portalId,
            estateId: selectedEstate.id,
            matterId: selectedMatterIds[0],
          },
        },
      });
      // close the modal when linking a single matters
      onExitClick();
    }
    syncDecisionVaultMatters({
      variables: {
        input: {
          portalId,
          matterIds: selectedMatterIds,
        },
      },
    });
  };

  const isLoading =
    isLoadingMattersData || syncDecisionVaultMattersExecuting || linkDecisionVaultMatterExecuting;

  return (
    <Box className={classes.container}>
      <Typography className={classes.header} variant="h4">
        {selectedEstate ? 'Link Matter' : 'Sync Matters '}
        from DecisionVault
      </Typography>
      {!isSyncingSelectMatters && (
        <Box>
          <ButtonGroup disableElevation variant="contained" orientation="vertical">
            <Button
              variant="contained"
              className={classes.button}
              disabled={isLoading}
              onClick={onSyncAllClick}
            >
              Sync All Matters
            </Button>
            <Button
              variant="contained"
              className={classes.button}
              disabled={isLoading}
              onClick={() => {
                setIsSyncingSelectMatters(true);
              }}
            >
              Select Matters to Sync
            </Button>
          </ButtonGroup>
        </Box>
      )}
      {isSyncingSelectMatters && (
        <Box>
          <Box>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box className={classes.searchBox}>
                <Controller
                  name="matterName"
                  control={control}
                  render={({ field: { ref, ...field } }) => (
                    <FormTextField
                      {...field}
                      inputRef={ref}
                      autoFocus
                      placeholder="Search matters by name"
                      onKeyDown={onKeyDownHandler}
                      InputProps={{
                        style: {
                          height: '56px',
                          borderRadius: '999px',
                          marginTop: '-10px',
                        },
                      }}
                    />
                  )}
                />
                <Button
                  className={classes.submitButton}
                  variant="contained"
                  type="submit"
                  aria-label="search"
                  onSubmit={handleSubmit(onSubmit)}
                  disabled={isLoading}
                >
                  <Typography variant="body1">Search</Typography>
                </Button>
              </Box>
              {mattersData?.data && mattersData?.data.length > 0 && (
                <SyncDecisionVaultModalFormSearchResultGrid
                  mattersData={mattersData?.data}
                  isLoading={isLoading}
                  setSelectedMatterIds={setSelectedMatterIds}
                  selectedEstate={selectedEstate}
                />
              )}
            </form>
          </Box>
          <Box className={classes.modalFooter}>
            <Button
              variant="contained"
              className={classes.button}
              disabled={isLoading || selectedMatterIds.length === 0}
              onClick={onSyncSelectedClick}
            >
              {selectedEstate ? 'Link' : 'Sync'} Selected
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
